import React from 'react';
import './NeonEagle.css';
function NeonEagle({ color = '#00ffff', height = 160, speed = '6s', alertCount = 0 }) {
  const [leftFlash, setLeftFlash] = React.useState(false);
  const [rightFlash, setRightFlash] = React.useState(false);

  const { glowColor } = React.useMemo(() => {
    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
    const alpha = clamp(0.18 + alertCount * 0.02, 0.18, 0.65);
    const toRgb = (hex) => {
      let h = hex.replace('#', '');
      if (h.length === 3) h = h.split('').map(c => c + c).join('');
      const r = parseInt(h.substring(0, 2), 16) || 0;
      const g = parseInt(h.substring(2, 4), 16) || 255;
      const b = parseInt(h.substring(4, 6), 16) || 255;
      return `${r}, ${g}, ${b}`;
    };
    return { glowColor: `rgba(${toRgb(color)}, ${alpha.toFixed(2)})` };
  }, [color, alertCount]);

  const handleFlash = (side) => {
    if (side === 'left') {
      setLeftFlash(true);
      setTimeout(() => setLeftFlash(false), 450);
    } else {
      setRightFlash(true);
      setTimeout(() => setRightFlash(false), 450);
    }
  };

  return (
    <div className="neon-eagle" style={{ height, ['--neon-color']: color, ['--speed']: speed, ['--glow-color']: glowColor }}>
      <svg viewBox="-160 -100 320 200" width="100%" height="100%" role="img" aria-label="Neon geometric eagle">
        <defs>
          <filter id="neonGlowE" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="bodyGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.15" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>
          {/* Scanlines pattern */}
          <pattern id="scan" width="6" height="6" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="6" height="6" fill="transparent" />
            <rect x="0" y="0" width="6" height="1" fill={color} opacity="0.08" />
          </pattern>
        </defs>

        <g className="eagle-group float">
          {/* Body and head */}
          <polygon className="neon-stroke" points="-18,6 0,-10 18,6 10,30 -10,30" fill="url(#bodyGrad)" strokeWidth="2.5" />
          <polyline className="neon-stroke" points="0,-10 6,-16 18,-14" fill="none" strokeWidth="2.5" />
          <circle cx="6" cy="-13" r="1.8" className="neon-stroke" fill="none" strokeWidth="1.4" />

          {/* Right wing (anchored at shoulder 12,0) */}
          <g className={`wing wing-right ${rightFlash ? 'flash' : ''}`} transform="translate(12,0)" onClick={() => handleFlash('right')}>
            <polyline className="neon-stroke" points="0,0 30,-10 60,-6 90,-14 120,-8" fill="none" strokeWidth="2.5" />
            <polyline className="neon-stroke" points="0,6 28,0 56,6 84,0 112,6" fill="none" strokeWidth="2" />
            <polyline className="neon-stroke feathers" points="20,10 18,24 26,24 24,10" fill="none" strokeWidth="1.6" />
            <polyline className="neon-stroke feathers" points="44,12 42,28 50,28 48,12" fill="none" strokeWidth="1.6" />
            <polyline className="neon-stroke feathers" points="68,10 66,26 74,26 72,10" fill="none" strokeWidth="1.6" />
            <polyline className="neon-stroke feathers" points="92,12 90,28 98,28 96,12" fill="none" strokeWidth="1.6" />
          </g>

          {/* Left wing (anchored at -12,0, mirrored geometry without scale) */}
          <g className={`wing wing-left ${leftFlash ? 'flash' : ''}`} transform="translate(-12,0)" onClick={() => handleFlash('left')}>
            <polyline className="neon-stroke" points="0,0 -30,-10 -60,-6 -90,-14 -120,-8" fill="none" strokeWidth="2.5" />
            <polyline className="neon-stroke" points="0,6 -28,0 -56,6 -84,0 -112,6" fill="none" strokeWidth="2" />
            <polyline className="neon-stroke feathers" points="-20,10 -18,24 -26,24 -24,10" fill="none" strokeWidth="1.6" />
            <polyline className="neon-stroke feathers" points="-44,12 -42,28 -50,28 -48,12" fill="none" strokeWidth="1.6" />
            <polyline className="neon-stroke feathers" points="-68,10 -66,26 -74,26 -72,10" fill="none" strokeWidth="1.6" />
            <polyline className="neon-stroke feathers" points="-92,12 -90,28 -98,28 -96,12" fill="none" strokeWidth="1.6" />
          </g>

          {/* Tail */}
          <polyline className="neon-stroke" points="-10,30 0,40 10,30" fill="none" strokeWidth="2" />
          <polyline className="neon-stroke feathers" points="-6,34 -6,44 -2,44 -2,34" fill="none" strokeWidth="1.4" />
          <polyline className="neon-stroke feathers" points="2,34 2,44 6,44 6,34" fill="none" strokeWidth="1.4" />

          {/* Ambient ground grid */}
          <g className="ground">
            <line x1="-160" y1="70" x2="160" y2="70" className="neon-stroke faint" strokeWidth="1" />
            {Array.from({ length: 7 }).map((_, i) => (
              <line key={i} x1={-150 + i * 50} y1={70} x2={-110 + i * 50} y2={40} className="neon-stroke faint" strokeWidth="1" />
            ))}
          </g>

          {/* Scan lines overlay */}
          <g className="scanlines" pointerEvents="none">
            <rect x="-160" y="-100" width="320" height="200" fill="url(#scan)" />
          </g>
        </g>
      </svg>
    </div>
  );
}

export default NeonEagle;
